import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  FormGroup,
  LinearProgress,
  Chip,
  Stack,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LanguageIcon from '@mui/icons-material/Language';
import { buildOrgLookupQuery, buildWebsiteLookupQueries, extractCorporateEmailDomain, firecrawlOrgLookup, firecrawlWebsiteLookupMulti, rankOrgCandidates, rankWebsiteCandidates } from '../../Api/Firecrawl';
import { toast } from 'react-toastify';

const LOOKUP_SOURCES = ['sgpbusiness.com', 'Singapore registry pages'];
const EXTRACT_FIELDS = ['UEN', 'Entity name', 'Address', 'SSIC codes', 'Entity type', 'Status'];

const SearchDetailRow = ({ label, children }) => (
  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
    <Typography
      sx={{
        fontSize: '11px',
        fontWeight: 600,
        color: '#6B7280',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        minWidth: 72,
        pt: 0.25,
        flexShrink: 0,
      }}
    >
      {label}
    </Typography>
    <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
  </Box>
);

const DirectorySearchProgress = ({ organizationName }) => {
  const query = buildOrgLookupQuery(organizationName);

  return (
    <Box
      sx={{
        width: '100%',
        border: '1px solid #BFDBFE',
        borderRadius: '8px',
        backgroundColor: '#F8FAFF',
        overflow: 'hidden',
      }}
    >
      <LinearProgress
        sx={{
          height: 3,
          backgroundColor: '#DBEAFE',
          '& .MuiLinearProgress-bar': { backgroundColor: '#3B82F6' },
        }}
      />
      <Box sx={{ px: 2, py: 1.75, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1E40AF' }}>
          Searching Singapore business directories
        </Typography>

        <SearchDetailRow label="Query">
          <Typography
            component="code"
            sx={{
              fontSize: '12px',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              color: '#1F2937',
              backgroundColor: '#EFF6FF',
              px: 1,
              py: 0.5,
              borderRadius: '4px',
              display: 'block',
              wordBreak: 'break-word',
            }}
          >
            {query}
          </Typography>
        </SearchDetailRow>

        <SearchDetailRow label="Sources">
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {LOOKUP_SOURCES.map((source) => (
              <Chip
                key={source}
                label={source}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '11px',
                  backgroundColor: '#EFF6FF',
                  color: '#1D4ED8',
                  border: '1px solid #BFDBFE',
                }}
              />
            ))}
          </Stack>
        </SearchDetailRow>

        <SearchDetailRow label="Extract">
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {EXTRACT_FIELDS.map((field) => (
              <Chip
                key={field}
                label={field}
                size="small"
                variant="outlined"
                sx={{ height: 22, fontSize: '11px', color: '#374151', borderColor: '#D1D5DB' }}
              />
            ))}
          </Stack>
        </SearchDetailRow>
      </Box>
    </Box>
  );
};

const WebsiteSearchProgress = ({ queries = [] }) => (
  <Box
    sx={{
      mb: 2,
      border: '1px solid #BFDBFE',
      borderRadius: '8px',
      backgroundColor: '#F8FAFF',
      overflow: 'hidden',
    }}
  >
    <LinearProgress
      sx={{
        height: 3,
        backgroundColor: '#DBEAFE',
        '& .MuiLinearProgress-bar': { backgroundColor: '#3B82F6' },
      }}
    />
    <Box sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LanguageIcon sx={{ fontSize: 18, color: '#3B82F6' }} />
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1E40AF' }}>
          Searching for company websites
        </Typography>
      </Box>

      <SearchDetailRow label="Queries">
        <Stack spacing={0.75}>
          {queries.map((item) => (
            <Box key={`${item.label}-${item.query}`}>
              <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#6B7280', mb: 0.25 }}>
                {item.label}
              </Typography>
              <Typography
                component="code"
                sx={{
                  fontSize: '12px',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  color: '#1F2937',
                  backgroundColor: '#EFF6FF',
                  px: 1,
                  py: 0.5,
                  borderRadius: '4px',
                  display: 'block',
                  wordBreak: 'break-word',
                }}
              >
                {item.query}
              </Typography>
            </Box>
          ))}
        </Stack>
      </SearchDetailRow>

      <SearchDetailRow label="Via">
        <Chip
          label="Firecrawl web search"
          size="small"
          sx={{
            height: 22,
            fontSize: '11px',
            backgroundColor: '#EFF6FF',
            color: '#1D4ED8',
            border: '1px solid #BFDBFE',
          }}
        />
      </SearchDetailRow>
    </Box>
  </Box>
);

const resolveContactEmail = (contactEmail) => {
  if (contactEmail) return String(contactEmail).trim();
  try {
    return String(JSON.parse(localStorage.getItem('userLogindata') || '{}')?.email || '').trim();
  } catch {
    return '';
  }
};

const FIELD_DEFINITIONS = [
  { key: 'registerEntity', label: 'Registered Entity Name' },
  { key: 'incorporationNumber', label: 'Registration or Incorporation Number' },
  { key: 'address', label: 'Physical Address' },
  { key: 'companyWebsite', label: 'Company Website' },
  { key: 'selectedCountry', label: 'Registered Country' },
  { key: 'entityType', label: 'Entity Type' },
  { key: 'businessConstitution', label: 'Business Constitution' },
  { key: 'companyType', label: 'Company Type' },
  { key: 'status', label: 'Status' },
  { key: 'registrationDate', label: 'Registration Date' },
  { key: 'registrationIncorporationDate', label: 'Registration Incorporation Date' },
  { key: 'uenIssueDate', label: 'UEN Issue Date' },
  { key: 'postalCode', label: 'Postal Code' },
  { key: 'primarySsicCode', label: 'Primary SSIC Code' },
  { key: 'primarySsicDescription', label: 'Primary SSIC Description' },
  { key: 'secondarySsicCode', label: 'Secondary SSIC Code' },
  { key: 'secondarySsicDescription', label: 'Secondary SSIC Description' },
  { key: 'noOfOfficers', label: 'No of Officers' },
  { key: 'formerNames', label: 'Former Names' },
];

const formatDate = (dateValue) => {
  if (!dateValue) return '';
  try {
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }
    if (typeof dateValue === 'string' && dateValue.includes('T')) {
      return dateValue.split('T')[0];
    }
    if (dateValue instanceof Date && !Number.isNaN(dateValue.getTime())) {
      return dateValue.toISOString().split('T')[0];
    }
    return String(dateValue).trim();
  } catch {
    return String(dateValue || '').trim();
  }
};

const formatFormerNames = (formerNamesValue) => {
  if (!formerNamesValue) return '';
  try {
    if (Array.isArray(formerNamesValue)) {
      const valid = formerNamesValue.filter(
        (name) => name && String(name).toLowerCase() !== 'na' && String(name).trim() !== ''
      );
      return valid.length > 0 ? valid.join(', ') : '';
    }
    if (typeof formerNamesValue === 'string') {
      const parsed = JSON.parse(formerNamesValue);
      if (Array.isArray(parsed)) {
        const valid = parsed.filter(
          (name) => name && String(name).toLowerCase() !== 'na' && String(name).trim() !== ''
        );
        return valid.length > 0 ? valid.join(', ') : '';
      }
      return formerNamesValue;
    }
    return String(formerNamesValue);
  } catch {
    return String(formerNamesValue || '');
  }
};

/** Map Firecrawl JSON payload to the shared organizationData shape. */
export const mapJsonToOrganizationData = (json) => {
  if (!json || typeof json !== 'object') return {};

  const registrationDate = formatDate(json.registration_date);
  const data = {
    registerEntity: String(json.registered_entity_name || '').trim(),
    incorporationNumber: String(json.uen || '').trim().toUpperCase(),
    address: String(json.registered_address || '').trim(),
    companyWebsite: String(json.website || '').trim(),
    selectedCountry: 'Singapore',
    entityType: String(json.entity_type || '').trim(),
    businessConstitution: String(json.business_constitution || '').trim(),
    companyType: String(json.company_type || '').trim(),
    status: String(json.status || '').trim(),
    registrationDate,
    registrationIncorporationDate: registrationDate,
    uenIssueDate: formatDate(json.uen_issue_date),
    postalCode: String(json.postal_code || '').trim(),
    primarySsicCode: String(json.primary_ssic_code || '').trim(),
    primarySsicDescription: String(json.primary_ssic_description || '').trim(),
    secondarySsicCode: String(json.secondary_ssic_code || '').trim(),
    secondarySsicDescription: String(json.secondary_ssic_description || '').trim(),
    noOfOfficers:
      json.no_of_officers !== undefined && json.no_of_officers !== null
        ? String(json.no_of_officers)
        : '',
    formerNames: formatFormerNames(json.former_names),
  };

  return Object.fromEntries(
    Object.entries(data).filter(([, value]) => value !== '' && value !== null && value !== undefined)
  );
};

const buildFieldRows = (organizationData) =>
  FIELD_DEFINITIONS.map((def) => ({
    ...def,
    value: organizationData[def.key],
  })).filter((row) => row.value !== '' && row.value !== null && row.value !== undefined);

const OrganizationLookup = ({
  organizationName,
  registeredEntity = '',
  contactEmail = '',
  onAcceptFill,
  disabled = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogStep, setDialogStep] = useState(1);
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidateIndex, setSelectedCandidateIndex] = useState(0);
  const [selectedFieldKeys, setSelectedFieldKeys] = useState({});
  const [websiteCandidates, setWebsiteCandidates] = useState([]);
  const [selectedWebsiteKeys, setSelectedWebsiteKeys] = useState({});
  const [pendingSelectedData, setPendingSelectedData] = useState(null);
  const [websiteSearchQueries, setWebsiteSearchQueries] = useState([]);

  const trimmedName = String(organizationName || '').trim();
  const trimmedRegisteredEntity = String(registeredEntity || '').trim();
  const resolvedContactEmail = resolveContactEmail(contactEmail);
  const emailDomainHint = extractCorporateEmailDomain(resolvedContactEmail);
  const showCompanyStep = candidates.length > 1;

  const organizationData = useMemo(() => {
    const candidate = candidates[selectedCandidateIndex];
    return candidate ? mapJsonToOrganizationData(candidate.json) : {};
  }, [candidates, selectedCandidateIndex]);

  const fieldRows = useMemo(() => buildFieldRows(organizationData), [organizationData]);

  const resetDialog = () => {
    setDialogOpen(false);
    setDialogStep(1);
    setCandidates([]);
    setSelectedCandidateIndex(0);
    setSelectedFieldKeys({});
    setWebsiteCandidates([]);
    setSelectedWebsiteKeys({});
    setPendingSelectedData(null);
    setWebsiteSearchQueries([]);
  };

  const applySelectedData = (selectedData, websiteNote = '') => {
    if (onAcceptFill) {
      onAcceptFill(selectedData);
    }
    const appliedCount = Object.keys(selectedData).length;
    toast.success(
      `Applied ${appliedCount} field${appliedCount === 1 ? '' : 's'} to the form${websiteNote}`
    );
    resetDialog();
  };

  const initializeFieldReview = (candidateList, candidateIndex) => {
    const candidate = candidateList[candidateIndex];
    if (!candidate) return;
    const mapped = mapJsonToOrganizationData(candidate.json);
    const rows = buildFieldRows(mapped);
    const initialSelection = {};
    rows.forEach((row) => {
      initialSelection[row.key] = true;
    });
    setSelectedCandidateIndex(candidateIndex);
    setSelectedFieldKeys(initialSelection);
    setDialogStep(candidateList.length > 1 ? 2 : 1);
    setDialogOpen(true);
  };

  const handleAutoFill = async () => {
    if (!trimmedName) {
      toast.warning('Please enter an organization name first');
      return;
    }

    setLoading(true);
    try {
      const response = await firecrawlOrgLookup(trimmedName);
      const ranked = rankOrgCandidates(response, 3);
      if (!ranked.length) {
        toast.info('No matching company records found. Try a different organization name.');
        return;
      }

      setCandidates(ranked);
      setSelectedCandidateIndex(0);

      if (ranked.length === 1) {
        initializeFieldReview(ranked, 0);
      } else {
        setDialogStep(1);
        setDialogOpen(true);
      }
    } catch (error) {
      console.error('Firecrawl org lookup error:', error);
      toast.error(
        error?.response?.data?.error ||
          'Failed to look up organization details. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyNext = () => {
    initializeFieldReview(candidates, selectedCandidateIndex);
  };

  const handleFieldToggle = (key) => {
    setSelectedFieldKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleConfirmFill = async () => {
    const selectedData = {};
    fieldRows.forEach((row) => {
      if (selectedFieldKeys[row.key]) {
        selectedData[row.key] = row.value;
      }
    });

    const count = Object.keys(selectedData).length;
    if (count === 0) {
      toast.warning('Select at least one field to fill');
      return;
    }

    const hadWebsiteInLookup = fieldRows.some((row) => row.key === 'companyWebsite');
    if (selectedData.companyWebsite || hadWebsiteInLookup) {
      applySelectedData(selectedData);
      return;
    }

    const searchEntity = selectedData.registerEntity || trimmedRegisteredEntity || trimmedName;
    const searchParams = {
      registeredName: searchEntity,
      registrationId: selectedData.incorporationNumber,
      contactEmail: resolvedContactEmail,
    };
    const queries = buildWebsiteLookupQueries(searchParams);

    if (!queries.length) {
      toast.warning('Add a registered entity name or use a work contact email to search for websites');
      return;
    }

    setConfirmLoading(true);
    setWebsiteSearchQueries(queries);
    try {
      const response = await firecrawlWebsiteLookupMulti(searchParams);
      const ranked = rankWebsiteCandidates(response, searchEntity, 5, resolvedContactEmail);

      if (!ranked.length) {
        applySelectedData(selectedData);
        toast.info('No website candidates found. You can enter it manually.');
        return;
      }

      const initialWebsiteSelection = {};
      ranked.forEach((candidate, index) => {
        initialWebsiteSelection[candidate.domain] = index === 0;
      });

      setPendingSelectedData(selectedData);
      setWebsiteCandidates(ranked);
      setSelectedWebsiteKeys(initialWebsiteSelection);
      setDialogStep(3);
    } catch (error) {
      console.error('Website lookup error:', error);
      applySelectedData(selectedData);
      toast.info('Could not search for a website. You can enter it manually.');
    } finally {
      setConfirmLoading(false);
      setWebsiteSearchQueries([]);
    }
  };

  const handleWebsiteToggle = (domain) => {
    setSelectedWebsiteKeys((prev) => ({ ...prev, [domain]: !prev[domain] }));
  };

  const handleWebsiteConfirm = () => {
    const selectedData = { ...(pendingSelectedData || {}) };
    const chosen = websiteCandidates.filter((candidate) => selectedWebsiteKeys[candidate.domain]);
    if (chosen.length === 0) {
      toast.warning('Select at least one website, or use Skip');
      return;
    }
    selectedData.companyWebsite = chosen[0].domain;
    if (chosen.length > 1) {
      selectedData.additionalWebsites = chosen.slice(1).map((candidate) => candidate.domain);
    }
    applySelectedData(
      selectedData,
      chosen.length > 1 ? ` (including ${chosen.length} websites)` : ' (including website)'
    );
  };

  const handleWebsiteSkip = () => {
    if (pendingSelectedData) {
      applySelectedData(pendingSelectedData);
    }
  };

  const handleWebsiteBack = () => {
    setWebsiteCandidates([]);
    setSelectedWebsiteKeys({});
    setPendingSelectedData(null);
    setDialogStep(showCompanyStep ? 2 : 1);
  };

  const checkedCount = fieldRows.filter((row) => selectedFieldKeys[row.key]).length;
  const selectedWebsiteCount = websiteCandidates.filter(
    (candidate) => selectedWebsiteKeys[candidate.domain]
  ).length;

  return (
    <Box sx={{ mb: 1, width: '100%' }}>
      {loading ? (
        <DirectorySearchProgress organizationName={trimmedName} />
      ) : (
        <Button
          variant="outlined"
          fullWidth
          onClick={handleAutoFill}
          disabled={disabled || !trimmedName}
          startIcon={<SearchIcon />}
          sx={{
            borderColor: '#3B82F6',
            color: '#3B82F6',
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '13px',
            py: 1.1,
            '&:hover': {
              borderColor: '#2563EB',
              backgroundColor: '#EFF6FF',
            },
            '&:disabled': {
              borderColor: '#E5E7EB',
              color: '#9CA3AF',
            },
          }}
        >
          Auto-fill Details
        </Button>
      )}
      <Typography variant="caption" sx={{ display: 'block', color: '#6B7280', mt: 0.5 }}>
        We&apos;ll search Singapore business directories for UEN and company details.
      </Typography>

      <Dialog
        open={dialogOpen}
        onClose={resetDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        {dialogOpen && (
          <>
            <DialogTitle sx={{ fontWeight: 600, color: '#111827', pb: 1 }}>
              {dialogStep === 3
                ? 'Choose Website'
                : dialogStep === 1 && showCompanyStep
                  ? 'Choose Company'
                  : 'Review Fields to Fill'}
            </DialogTitle>
            <DialogContent dividers>
              {dialogStep === 3 && (
                <Box>
                  {confirmLoading && websiteSearchQueries.length > 0 && (
                    <WebsiteSearchProgress queries={websiteSearchQueries} />
                  )}
                  {!confirmLoading && (
                    <>
                      <Typography variant="body2" sx={{ color: '#6B7280', mb: 1 }}>
                        We found {websiteCandidates.length} possible website
                        {websiteCandidates.length === 1 ? '' : 's'} using the registered entity
                        {emailDomainHint ? ' and contact email domain' : ''}. Select one or more —
                        the first selected becomes your primary domain.
                      </Typography>
                      {emailDomainHint && (
                        <Typography variant="caption" sx={{ display: 'block', color: '#6B7280', mb: 1.5 }}>
                          Contact email hint: @{emailDomainHint}
                        </Typography>
                      )}
                      <FormGroup>
                        {websiteCandidates.map((candidate) => (
                          <Box
                            key={candidate.domain}
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              py: 0.75,
                              borderBottom: '1px solid #F3F4F6',
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={!!selectedWebsiteKeys[candidate.domain]}
                                  onChange={() => handleWebsiteToggle(candidate.domain)}
                                />
                              }
                              label={
                                <Box
                                  sx={{
                                    p: 1.25,
                                    border: '1px solid',
                                    borderColor: selectedWebsiteKeys[candidate.domain]
                                      ? '#3B82F6'
                                      : '#E5E7EB',
                                    borderRadius: '8px',
                                    width: '100%',
                                  }}
                                >
                                  <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>
                                    {candidate.domain}
                                  </Typography>
                                  <Typography sx={{ fontSize: '11px', color: '#6B7280', mt: 0.25 }}>
                                    Found via {candidate.searchLabel}
                                  </Typography>
                                  {candidate.title && candidate.title !== candidate.domain && (
                                    <Typography sx={{ fontSize: '12px', color: '#374151', mt: 0.25 }}>
                                      {candidate.title}
                                    </Typography>
                                  )}
                                  {candidate.description && (
                                    <Typography sx={{ fontSize: '12px', color: '#6B7280', mt: 0.5 }}>
                                      {candidate.description}
                                    </Typography>
                                  )}
                                </Box>
                              }
                              sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
                            />
                          </Box>
                        ))}
                      </FormGroup>
                      <Typography variant="caption" sx={{ display: 'block', color: '#6B7280', mt: 1 }}>
                        {selectedWebsiteCount} of {websiteCandidates.length} selected
                      </Typography>
                    </>
                  )}
                </Box>
              )}

              {dialogStep === 1 && showCompanyStep && (
                <Box>
                  <Typography variant="body2" sx={{ color: '#6B7280', mb: 2 }}>
                    Multiple matches found for &quot;{trimmedName}&quot;. Select the correct company.
                  </Typography>
                  <RadioGroup
                    value={String(selectedCandidateIndex)}
                    onChange={(e) => setSelectedCandidateIndex(Number(e.target.value))}
                  >
                    {candidates.map((candidate, idx) => (
                      <FormControlLabel
                        key={`${candidate.uen || candidate.registered_entity_name}-${idx}`}
                        value={String(idx)}
                        control={<Radio size="small" />}
                        label={
                          <Box
                            sx={{
                              p: 1.5,
                              border: '1px solid',
                              borderColor: selectedCandidateIndex === idx ? '#3B82F6' : '#E5E7EB',
                              borderRadius: '8px',
                              width: '100%',
                            }}
                          >
                            <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>
                              {candidate.registered_entity_name || 'Unknown Organization'}
                            </Typography>
                            <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>
                              UEN: {candidate.uen || 'N/A'} · Source: {candidate.source}
                            </Typography>
                            {candidate.address && (
                              <Typography sx={{ fontSize: '12px', color: '#374151', mt: 0.5 }}>
                                {candidate.address}
                              </Typography>
                            )}
                          </Box>
                        }
                        sx={{
                          alignItems: 'flex-start',
                          m: 0,
                          mb: 1.5,
                          width: '100%',
                          ml: 0,
                        }}
                      />
                    ))}
                  </RadioGroup>
                </Box>
              )}

              {(dialogStep === 2 || (dialogStep === 1 && !showCompanyStep)) && (
                <Box>
                  {confirmLoading && websiteSearchQueries.length > 0 && (
                    <WebsiteSearchProgress queries={websiteSearchQueries} />
                  )}
                  {candidates[selectedCandidateIndex] && (
                    <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>
                        {candidates[selectedCandidateIndex].registered_entity_name}
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>
                        UEN: {candidates[selectedCandidateIndex].uen || 'N/A'} ·{' '}
                        {candidates[selectedCandidateIndex].source}
                      </Typography>
                    </Box>
                  )}

                  {fieldRows.length === 0 ? (
                    <Typography variant="body2" sx={{ color: '#6B7280' }}>
                      No extractable fields found for this company.
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ color: '#6B7280', mb: 1.5 }}>
                        Select which fields to apply to your form ({checkedCount} of {fieldRows.length}{' '}
                        selected).
                      </Typography>
                      <FormGroup>
                        {fieldRows.map((row) => (
                          <Box
                            key={row.key}
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              py: 0.75,
                              borderBottom: '1px solid #F3F4F6',
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={!!selectedFieldKeys[row.key]}
                                  onChange={() => handleFieldToggle(row.key)}
                                />
                              }
                              label={
                                <Box>
                                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                                    {row.label}
                                  </Typography>
                                  <Typography sx={{ fontSize: '12px', color: '#6B7280', wordBreak: 'break-word' }}>
                                    {row.value}
                                  </Typography>
                                </Box>
                              }
                              sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
                            />
                          </Box>
                        ))}
                      </FormGroup>
                    </>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #E5E7EB' }}>
              <Button onClick={resetDialog} sx={{ textTransform: 'none', color: '#6B7280' }}>
                Cancel
              </Button>
              {dialogStep === 3 && (
                <>
                  <Button onClick={handleWebsiteBack} sx={{ textTransform: 'none' }}>
                    Back
                  </Button>
                  <Button onClick={handleWebsiteSkip} sx={{ textTransform: 'none', color: '#6B7280' }}>
                    Skip
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleWebsiteConfirm}
                    disabled={confirmLoading || websiteCandidates.length === 0 || selectedWebsiteCount === 0}
                    sx={{
                      backgroundColor: '#1D3DFC',
                      textTransform: 'none',
                      '&:hover': { backgroundColor: '#0E2BC0' },
                    }}
                  >
                    Confirm & Fill
                  </Button>
                </>
              )}
              {dialogStep !== 3 && dialogStep === 2 && showCompanyStep && (
                <Button onClick={() => setDialogStep(1)} sx={{ textTransform: 'none' }}>
                  Back
                </Button>
              )}
              {dialogStep !== 3 && dialogStep === 1 && showCompanyStep ? (
                <Button
                  variant="contained"
                  onClick={handleCompanyNext}
                  sx={{
                    backgroundColor: '#1D3DFC',
                    textTransform: 'none',
                    '&:hover': { backgroundColor: '#0E2BC0' },
                  }}
                >
                  Next
                </Button>
              ) : dialogStep !== 3 ? (
                <Button
                  variant="contained"
                  onClick={handleConfirmFill}
                  disabled={fieldRows.length === 0 || checkedCount === 0 || confirmLoading}
                  sx={{
                    backgroundColor: '#1D3DFC',
                    textTransform: 'none',
                    '&:hover': { backgroundColor: '#0E2BC0' },
                  }}
                >
                  {confirmLoading ? 'Searching websites...' : 'Confirm & Fill'}
                </Button>
              ) : null}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default OrganizationLookup;
